// Shared profile-completion helpers used by AuthGate (gate decisions) and
// the Dashboard (completion-state UI). Single source of truth so the gate
// rules and the dashboard checklist can never disagree.

import type { UserProfile } from "../auth/UserProfileProvider.tsx";

// Required keys on a Phase-1A safe_mode_config. Mirrors the strict set
// validated by public.configure_safe_mode (Migration 0019). Currency is
// locked to GHS in Phase 1A.
export const SAFE_MODE_NUMERIC_KEYS: readonly string[] = [
  "daily_loss_limit",
  "weekly_loss_limit",
  "drawdown_protection_pct",
  "cool_off_loss_count",
  "cool_off_hours",
  "weekly_recommended_stake_budget",
];

export function isSafeModeConfigured(profile: UserProfile): boolean {
  const cfg = profile.safe_mode_config;
  if (cfg === null || typeof cfg !== "object" || Array.isArray(cfg)) {
    return false;
  }
  const obj = cfg as Record<string, unknown>;
  if (obj.currency !== "GHS") return false;
  for (const key of SAFE_MODE_NUMERIC_KEYS) {
    if (typeof obj[key] !== "number") return false;
  }
  return true;
}

// Three-way Phantom Bet completion status driven off the same data AuthGate
// uses. The dashboard renders one of three cards based on this value.
export type PhantomStatus = "active" | "opted_out" | "pending";

export function getPhantomStatus(
  profile: UserProfile | null,
  hasActiveSession: boolean,
): PhantomStatus {
  if (profile?.phantom_opt_out_at !== null && profile?.phantom_opt_out_at !== undefined) {
    return "opted_out";
  }
  if (hasActiveSession) {
    return "active";
  }
  return "pending";
}

// Onboarding checklist row shape consumed by the dashboard. Each row carries
// a label, an optional detail line (e.g. country code, platform slug), and a
// `done` flag derived from the same source AuthGate uses to advance gates.
export interface OnboardingChecklistItem {
  key: "age" | "jurisdiction" | "safe_mode" | "platform" | "phantom";
  label: string;
  detail: string | null;
  done: boolean;
}

export function getOnboardingChecklist(
  profile: UserProfile | null,
  hasActiveSession: boolean,
): OnboardingChecklistItem[] {
  const phantomStatus = getPhantomStatus(profile, hasActiveSession);
  return [
    {
      key: "age",
      label: "Age confirmed",
      detail: profile?.age_attestation_country_code ?? null,
      done: profile?.confirmed_legal_age === true,
    },
    {
      key: "jurisdiction",
      label: "Supported jurisdiction confirmed",
      detail: profile?.country_code ?? null,
      done: profile?.country_code !== null && profile?.jurisdiction_supported === true,
    },
    {
      key: "safe_mode",
      label: "Safe Mode configured",
      detail: profile && isSafeModeConfigured(profile) ? "GHS limits saved" : null,
      done: profile !== null && isSafeModeConfigured(profile),
    },
    {
      key: "platform",
      label: "Platform selected",
      detail: profile?.preferred_platform ?? null,
      done: profile?.preferred_platform !== null && profile?.preferred_platform !== undefined,
    },
    {
      key: "phantom",
      label: phantomStatus === "opted_out" ? "Phantom Bet skipped" : "Phantom Bet started",
      detail:
        phantomStatus === "active"
          ? "Active session"
          : phantomStatus === "opted_out"
            ? "Opt-out recorded"
            : null,
      done: phantomStatus !== "pending",
    },
  ];
}
